import * as React from 'react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  Box,
  ActionBar,
  Button,
  OutlinedButton,
  PropTypes,
} from 'cs-kevlar-react'
import { ToolbarHeight } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import { N30 } from 'cs-kevlar-react/lib/es/theming/palette'
import { FormikProps } from 'formik'

type SaveButtonType = 'submit' | 'button'

interface EditModalActionBarProps<TType> {
  onDelete?: () => void
  onCancel?: () => void
  onSave?: () => void
  onSecondaryAction?: () => void
  formikProps?: FormikProps<TType>
  canDelete?: boolean
  canCancel?: boolean
  canSecondaryAction?: boolean
  saveText?: string
  saveButtonType?: SaveButtonType
  saveVariant?: 'text' | 'outlined' | 'contained'
  saveColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  deleteText?: string
  deleteVariant?: 'text' | 'outlined' | 'contained'
  deleteColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  secondaryActionText?: string
  secondaryActionVariant?: 'text' | 'outlined' | 'contained'
  secondaryActionColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  canSubmit?: boolean
  isLoading?: boolean
  loader?: JSX.Element
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      width: '100%',
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(10),
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
    },
    footer: {
      backgroundColor: theme.palette.background.paper,
      borderTop: `2px solid ${N30}`,
      height: ToolbarHeight,
    },
    saveButton: {
      marginLeft: theme.spacing(4),
    },
  }),
)

export const EditModalActionBar = <TType extends {}>({
  formikProps,
  onCancel,
  onDelete,
  onSave,
  onSecondaryAction,
  canDelete = true,
  canSecondaryAction = false,
  canCancel = true,
  canSubmit = true,
  isLoading = false,
  loader,
  saveText = 'Save',
  saveButtonType = 'submit',
  saveVariant = 'contained',
  saveColor = 'primary',
  deleteText = 'Delete',
  deleteVariant = 'text',
  deleteColor = 'primary',
  secondaryActionText = '',
  secondaryActionVariant = 'text',
  secondaryActionColor = 'primary',
}: EditModalActionBarProps<TType>) => {
  const classes = useStyles({})

  const disabled =
    (formikProps && formikProps.isSubmitting && canSubmit) || !canSubmit

  if (saveButtonType === 'button' && !onSave) {
    console.warn(
      'EditModalActionBar defines the Save button as type button without an onSave handler',
    )
  }

  const saveButtonProps = saveButtonType === 'button' ? { onClick: onSave } : {}

  return (
    <ActionBar className={classes.footer}>
      {canDelete && (
        <React.Fragment>
          <Button
            variant={deleteVariant}
            color={deleteColor}
            onClick={onDelete}
          >
            {deleteText}
          </Button>
          <Box className={classes.spacer}></Box>
        </React.Fragment>
      )}
      {canSecondaryAction && (
        <React.Fragment>
          <Button
            variant={secondaryActionVariant}
            color={secondaryActionColor}
            onClick={onSecondaryAction}
          >
            {secondaryActionText}
          </Button>
          <Box className={classes.spacer}></Box>
        </React.Fragment>
      )}
      {isLoading && loader && (
        <React.Fragment>
          {loader}
          <Box className={classes.spacer}></Box>
        </React.Fragment>
      )}
      {!canDelete && !canSecondaryAction && (!isLoading || !loader) && (
        <Box className={classes.spacer}></Box>
      )}
      {canCancel && <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>}
      <Button
        type={saveButtonType}
        variant={saveVariant}
        disabled={disabled}
        className={classes.saveButton}
        color={saveColor}
        {...saveButtonProps}
      >
        {saveText}
      </Button>
    </ActionBar>
  )
}
